import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ChevronRight, TrendingUp } from 'lucide-react';
import DriverExpressDetail from './DriverExpressDetail';

const API_URL = process.env.REACT_APP_API_URL;

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// Safe local date string — avoids UTC off-by-one in Peru (UTC-5)
function toLocalDateString(date) {
  const year  = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day   = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getWeekDates() {
  const today = new Date();
  const day   = today.getDay();
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - day + i);
    dates.push({ label: DAY_NAMES[i], date: toLocalDateString(d) });
  }
  return dates;
}

function WeeklyChart({ weekData, maxEarning }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
        Esta semana
      </div>
      <div className="flex items-end justify-between gap-1 h-28">
        {weekData.map(({ label, date, earned, count, isToday }) => {
          const pct = maxEarning > 0 ? (earned / maxEarning) * 100 : 0;
          return (
            <div key={date} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex flex-col items-center justify-end" style={{ height: '80px' }}>
                {earned > 0 && (
                  <div className="text-xs text-brand-green font-semibold mb-1 leading-none">
                    {earned.toFixed(0)}
                  </div>
                )}
                <div
                  className={`w-full rounded-t-lg transition-all ${
                    isToday ? 'bg-brand-primary' : earned > 0 ? 'bg-brand-green/60' : 'bg-gray-100'
                  }`}
                  style={{ height: `${Math.max(pct, earned > 0 ? 8 : 4)}%` }}
                />
              </div>
              <div className={`text-xs font-medium ${isToday ? 'text-brand-primary' : 'text-gray-400'}`}>
                {label}
              </div>
              {count > 0 && (
                <div className="text-xs text-gray-400">{count}</div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
        <span>Barras = S/ ganados · Número = entregas</span>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-brand-primary" />
          <span>Hoy</span>
        </div>
      </div>
    </div>
  );
}

export default function DriverExpressEarnings({ user_id, updateDelivery }) {
  const [history, setHistory]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [detailDelivery, setDetail] = useState(null);

  const weekDates = useMemo(() => getWeekDates(), []);
  const todayStr  = toLocalDateString(new Date());

  useEffect(() => {
    async function fetchHistory() {
      setLoading(true);
      try {
        // ── Safe local date math — never use toISOString() (UTC shifts date in Peru) ──
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const from = toLocalDateString(thirtyDaysAgo);

        const { data } = await axios.get(`${API_URL}/api/driver/express/deliveries`, {
          params: { user_id, date_from: from, date_to: todayStr },
        });
        // Only show completed deliveries in earnings
        setHistory(data.filter(d => d.status === 'entregado'));
      } catch (err) {
        console.error('[DriverEarnings] Error fetching history:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchHistory();
  }, [user_id]);

  // ── Weekly chart data ─────────────────────────────────────
  const weekData = useMemo(() => {
    return weekDates.map(({ label, date }) => {
      const dayDeliveries = history.filter(d => d.scheduled_date === date);
      const earned = dayDeliveries.reduce((s, d) => s + Number(d.driver_earnings), 0);
      return { label, date, earned, count: dayDeliveries.length, isToday: date === todayStr };
    });
  }, [history, weekDates, todayStr]);

  const maxEarning = Math.max(...weekData.map(d => d.earned), 1);

  const totalWeek           = weekData.reduce((s, d) => s + d.earned, 0);
  const totalMonth          = history.reduce((s, d) => s + Number(d.driver_earnings), 0);
  const totalDeliveriesWeek = weekData.reduce((s, d) => s + d.count, 0);

  // ── Group history by date ─────────────────────────────────
  const groupedHistory = useMemo(() => {
    const groups = {};
    history.forEach(d => {
      if (!groups[d.scheduled_date]) groups[d.scheduled_date] = [];
      groups[d.scheduled_date].push(d);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, items]) => ({ date, items }));
  }, [history]);

  function formatDateLabel(dateStr) {
    if (dateStr === todayStr) return 'Hoy';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        <div className="bg-white rounded-2xl h-40 animate-pulse border border-gray-100" />
        <div className="bg-white rounded-2xl h-24 animate-pulse border border-gray-100" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="text-xs text-gray-400 mb-1">Esta semana</div>
          <div className="text-2xl font-bold text-brand-green">
            S/ {totalWeek.toFixed(2)}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {totalDeliveriesWeek} entrega{totalDeliveriesWeek !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="text-xs text-gray-400 mb-1">Últimos 30 días</div>
          <div className="text-2xl font-bold text-brand-primary">
            S/ {totalMonth.toFixed(2)}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {history.length} entrega{history.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Weekly bar chart */}
      <WeeklyChart weekData={weekData} maxEarning={maxEarning} />

      {/* History list */}
      {groupedHistory.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
          <TrendingUp size={36} className="text-gray-200 mx-auto mb-3" />
          <div className="text-gray-500 font-medium">Sin ganancias registradas</div>
          <div className="text-xs text-gray-400 mt-1">
            Las entregas completadas aparecerán aquí
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
            Historial (últimos 30 días)
          </div>
          {groupedHistory.map(({ date, items }) => {
            const dayTotal = items.reduce((s, d) => s + Number(d.driver_earnings), 0);
            return (
              <div key={date} className="space-y-1.5">
                <div className="flex items-center justify-between px-1">
                  <span className="text-xs font-semibold text-gray-500">
                    {formatDateLabel(date)}
                  </span>
                  <span className="text-xs font-bold text-brand-green">
                    S/ {dayTotal.toFixed(2)}
                  </span>
                </div>
                {items.map(d => (
                  <button
                    key={d.id}
                    onClick={() => setDetail(d)}
                    className="w-full bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3 active:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {d.recipient_name}
                      </div>
                      <div className="text-xs text-gray-400 truncate">{d.dropoff_address}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{d.distance_km} km</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-bold text-brand-green">
                        S/ {Number(d.driver_earnings).toFixed(2)}
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-gray-300 shrink-0" />
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Detail modal — read only, no map button for completed deliveries */}
      {detailDelivery && (
        <DriverExpressDetail
          delivery={detailDelivery}
          user_id={user_id}
          onClose={() => setDetail(null)}
          onUpdate={(updated) => {
            updateDelivery(updated);
            setDetail(prev => ({ ...prev, ...updated }));
          }}
          onOpenMap={null}
        />
      )}
    </div>
  );
}